import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';
import { X, History, RefreshCw, CheckCircle2, Clock, ThumbsUp, ThumbsDown, Plus, User, ListTodo, ExternalLink, ChevronDown, Search, ShieldCheck, Pencil } from 'lucide-react';
import TimelinePanel from '../../../components/workspace/task/TimelinePanel';
import CreateTaskModal from '../../../components/workspace/task/CreateTaskModal';
import { SearchableSelect } from '../../../components/workspace/filters/FilterDropdowns';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function CustomDropdown({ value, onChange, options, placeholder = '— select —', disabled = false, zIndex = 500 }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    function onOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);
  const filtered = options.filter(o => !search || o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => String(o.value) === String(value));
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" disabled={disabled}
        onClick={() => { if (!disabled) { setOpen(o => !o); setSearch(''); } }}
        className="form-input"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left', width: '100%', opacity: disabled ? 0.5 : 1 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'var(--text-primary)' : 'var(--text-muted)' }}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.15)', marginTop: 2, display: 'flex', flexDirection: 'column', maxHeight: 260 }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-muted)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px' }}>
              <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search…" style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', color: 'var(--text-primary)' }} />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0
              ? <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>No results</div>
              : filtered.map(o => (
                <button key={o.value} type="button"
                  onClick={() => { onChange(String(o.value)); setOpen(false); setSearch(''); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, background: String(o.value) === String(value) ? 'var(--bg-muted)' : 'transparent', color: 'var(--text-primary)', border: 'none', cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {o.label}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'pending',         label: 'Pending' },
  { value: 'in_progress',     label: 'In Progress' },
  { value: 'awaiting_client', label: 'Awaiting Client' },
  { value: 'completed',       label: 'Completed' },
  { value: 'delayed',         label: 'Delayed' },
];

function statusColor(s) {
  if (!s || s === 'pending')      return { bg: '#FFF3E0', color: '#F57C00', label: 'Not Started' };
  if (s === 'completed')          return { bg: '#E8F5E9', color: '#388E3C', label: 'Completed' };
  if (s === 'in_progress')        return { bg: '#E3F2FD', color: '#1565C0', label: 'In Progress' };
  if (s === 'awaiting_client')    return { bg: '#F3E5F5', color: '#7B1FA2', label: 'Awaiting Client' };
  if (s === 'delayed')            return { bg: '#FFEBEE', color: '#C62828', label: 'Delayed' };
  return { bg: 'var(--bg-muted)', color: 'var(--text-secondary)', label: s };
}

// Tasks-module status vocabulary differs from contract_tasks' (open/blocked/
// done/cancelled vs pending/completed/awaiting_client/delayed) — statusColor
// above is for contract_tasks rows only, this is for linked_tasks rows.
function taskModuleStatusColor(s) {
  if (!s || s === 'open')  return { bg: '#FFF3E0', color: '#F57C00', label: 'Not Started' };
  if (s === 'in_progress') return { bg: '#E3F2FD', color: '#1565C0', label: 'In Progress' };
  if (s === 'blocked')     return { bg: '#F3E5F5', color: '#7B1FA2', label: 'Blocked' };
  if (s === 'done')        return { bg: '#E8F5E9', color: '#388E3C', label: 'Done' };
  if (s === 'cancelled')   return { bg: '#FFEBEE', color: '#C62828', label: 'Cancelled' };
  return { bg: 'var(--bg-muted)', color: 'var(--text-secondary)', label: s };
}

function checkColor(s) {
  if (s === 'approved')       return { bg: '#E8F5E9', color: '#388E3C', label: 'Approved' };
  if (s === 'rejected')       return { bg: '#FFEBEE', color: '#C62828', label: 'Rejected' };
  if (s === 'pending_review') return { bg: '#FFF9C4', color: '#F57F17', label: 'Pending Review' };
  return null;
}

function KpiCard({ label, value, sub, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 140,
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '16px 20px',
      borderTop: `3px solid ${accent}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

const trunc = (s, n = 25) => s && s.length > n ? s.slice(0, n) + '…' : (s || '');


function Badge({ done, total, color }) {
  if (total === 0) return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>;
  const bg = done === total ? '#E8F5E9' : '#FFF3E0';
  const fg = done === total ? '#388E3C' : (color || '#F57C00');
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 12, fontWeight: 700,
      background: bg, color: fg,
    }}>{done}/{total}</span>
  );
}

// Shared shape/size for View, Review, Approve, Reject — same style, only
// color varies per action.
const actionBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
};

/* ── Linked (Tasks-module) task card — real review mechanism ─── */
function LinkedTaskCard({ lt, svc, appId, agreementId, finalApproved, isAdmin, navigate, api, onSaved, onShowHistory, taskModalMembers, taskModalRoles, taskModalAreasByRole, myUserId }) {
  const stInfo = taskModuleStatusColor(lt.status);
  const chInfo = checkColor(lt.check_status);
  const [expanded, setExpanded] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [checking, setChecking] = useState(null); // null | 'approved' | 'rejected'
  const [error, setError] = useState(null);
  const isPendingReview = lt.check_status === 'pending_review';

  // Edit reuses the exact same modal/flow as the Tasks module's own Edit
  // button. The board's linked_tasks list only carries a few fields per
  // task (not description/priority/area/routine/estimated time), so we
  // fetch the full task fresh on click rather than pre-filling from the
  // partial `lt` object — using the partial object here would silently
  // blank those other fields on save, the same class of bug fixed earlier
  // where agreement_service_id was getting wiped on unrelated edits.
  const [editTask, setEditTask] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  async function openEdit() {
    setEditLoading(true);
    try {
      const full = await api.getTask(appId, lt.id);
      setEditTask(full);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load task');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleCheck(decision) {
    setChecking(decision); setError(null);
    try {
      await api.checkTask(appId, lt.id, {
        check_status:  decision,
        check_remarks: remarks || null,
      });
      setRemarks('');
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || 'Review failed');
    } finally {
      setChecking(null);
    }
  }

  return (
    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg-muted)', borderRadius: 6, border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
        <div
          onClick={() => navigate(`/apps/${appId}/tasks/${lt.id}`)}
          style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word', whiteSpace: 'normal', flex: 1, cursor: 'pointer' }}>
          {lt.title} {lt.task_number && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({lt.task_number})</span>}
        </div>
        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: stInfo.bg, color: stInfo.color, flexShrink: 0 }}>
          {stInfo.label}
        </span>
        {chInfo && (
          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, flexShrink: 0, background: chInfo.bg, color: chInfo.color }}>
            {chInfo.label}
          </span>
        )}
        {!expanded ? (
          <button
            onClick={() => setExpanded(true)}
            style={{
              ...actionBtnStyle,
              background: isPendingReview ? '#E8F5E9' : 'var(--bg-card)',
              color: isPendingReview ? '#388E3C' : 'var(--text-secondary)',
              border: isPendingReview ? '1px solid #A5D6A7' : '1px solid var(--border)',
            }}>{isPendingReview ? 'Review' : 'View'}</button>
        ) : (
          <button
            onClick={() => { setExpanded(false); setError(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
            <X size={14} />
          </button>
        )}
        {/* Hidden once this month has already been Final Approved and the
            client emailed, or once this specific task has already been
            approved by its reviewing manager — in both cases the backend
            blocks the save too, but hiding it avoids the user hitting that
            error at all. Once approved, only an admin or the task's own
            reviewing manager (can_review) may still edit it — a plain
            member/assignee should not be able to silently change checklist
            items or other fields on work already signed off on. */}
        {!finalApproved && (lt.check_status !== 'approved' || isAdmin || lt.can_review) && (
          <button
            onClick={openEdit}
            disabled={editLoading}
            title="Edit task"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0, opacity: editLoading ? 0.5 : 1 }}>
            <Pencil size={13} />
          </button>
        )}
        <button
          onClick={() => onShowHistory(svc, lt)}
          title="View history"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}>
          <History size={13} />
        </button>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {lt.assigned_user && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)' }}>
            <User size={11} />
            {lt.assigned_user.name}
          </span>
        )}
        {lt.due_on && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Due: {new Date(lt.due_on).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        )}
        {lt.completed_at && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Completion Date: {new Date(lt.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>

      {expanded && (<>
        {lt.checklist && lt.checklist.length > 0 && (
          <div style={{ marginTop: 6, minWidth: 0 }}>
            {lt.checklist.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '2px 0', minWidth: 0 }}>
                <input type="checkbox" checked={c.is_done} disabled style={{ flexShrink: 0, marginTop: 2, accentColor: '#388E3C', width: 12, height: 12 }} />
                <span style={{ fontSize: 11, color: c.is_done ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: c.is_done ? 'line-through' : 'none', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {c.text}
                </span>
              </div>
            ))}
          </div>
        )}

        {lt.checked_by_user && lt.checked_at && (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)' }}>
            <ShieldCheck size={11} />
            {chInfo ? chInfo.label : 'Reviewed'} by {lt.checked_by_user.name} on {new Date(lt.checked_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        )}
        {lt.check_remarks && (
          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            "{lt.check_remarks}"
          </div>
        )}

        {isPendingReview && (
          <div style={{ marginTop: 8 }}>
            <input
              type="text"
              className="form-input"
              maxLength={1000}
              placeholder="Remarks (optional)…"
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              style={{ fontSize: 12, marginBottom: 6, width: '100%', boxSizing: 'border-box' }} />
            {error && <div style={{ fontSize: 11, color: 'var(--neg)', marginBottom: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => handleCheck('rejected')}
                disabled={checking !== null}
                style={{ ...actionBtnStyle, gap: 6, background: '#C62828', color: '#fff', border: 'none', opacity: checking !== null ? 0.6 : 1 }}>
                <ThumbsDown size={12} /> {checking === 'rejected' ? 'Saving…' : 'Reject'}
              </button>
              <button
                onClick={() => handleCheck('approved')}
                disabled={checking !== null}
                style={{ ...actionBtnStyle, gap: 6, background: '#388E3C', color: '#fff', border: 'none', opacity: checking !== null ? 0.6 : 1 }}>
                <ThumbsUp size={12} /> {checking === 'approved' ? 'Saving…' : 'Approve'}
              </button>
            </div>
          </div>
        )}
      </>)}

      {/* Edit — the exact same modal/flow the Tasks module's own Edit
          button uses, opened with the freshly-fetched full task. */}
      <CreateTaskModal
        open={!!editTask}
        onClose={() => setEditTask(null)}
        members={taskModalMembers}
        roles={taskModalRoles}
        areasByRole={taskModalAreasByRole}
        editTask={editTask}
        defaultAssigneeUserId={myUserId}
        onUpdated={async () => { setEditTask(null); onSaved(); }}
      />
    </div>
  );
}

/* ── Service Row with inline form + review ─── */
function ServiceRow({ svc, month, year, agreementId, finalApproved, members, taskModalMembers, taskModalRoles, taskModalAreasByRole, isAdmin, myUserId, isMember, onSaved, onShowHistory }) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const api = useApi();

  const task               = svc.task;
  const currentStatus      = task?.status       || 'pending';

  // Create Task — reuses the real Tasks module modal (CreateTaskModal), pre-filled
  // to this agreement + service, instead of the old compliance-tasks mini-form.
  // That old form (api.createContractTask) auto-created at most one linked task
  // per service and hid this button once svc.task existed. CreateTaskModal has
  // no such ceiling — every click creates an independent, ordinary task via the
  // normal POST /tasks path, so multiple tasks per service work with no
  // duplication risk (no shared contract_tasks row, no per-service uniqueness
  // constraint on tasks.contract_id/agreement_service_id).
  const [createOpen, setCreateOpen] = useState(false);

  const stInfo   = statusColor(currentStatus);
  const isDone   = currentStatus === 'completed' || currentStatus === 'delayed';

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      marginBottom: 8,
      overflow: 'hidden',
      background: 'var(--bg-card)',
    }}>
      {/* Row header */}
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {/* Status indicator bar */}
          <div style={{
            width: 4, alignSelf: 'stretch', borderRadius: 4, flexShrink: 0, minHeight: 36,
            background: !task ? 'var(--border)' : isDone ? '#4CAF50' : stInfo.color,
          }} />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Top row: name + badges + actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {svc.service_name}
                </div>
              </div>

              {/* Status/check badges removed here — they described the legacy
                  contract_tasks slot, not the service itself, and read as
                  confusing "service status" next to the real per-task cards
                  below. Each task's own status/check badges are on its
                  LinkedTaskCard instead. */}

              {/* Action buttons — only New Task and History live at service
                  level now. View/Review/Approve/Reject moved onto each
                  task's own card (LinkedTaskCard) since review is per-task. */}
              {/* Hidden once this month has already been Final Approved and
                  the client emailed — the backend blocks this too, but
                  hiding it avoids the user hitting that error at all. */}
              {!finalApproved && (
                <button
                  onClick={() => setCreateOpen(true)}
                  title="Create task"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: 'var(--accent)', color: '#fff',
                    border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}><Plus size={12} /> Create Task</button>
              )}
              {/* No service-level history anymore — only per-task history,
                  on each LinkedTaskCard below. The legacy contract_tasks
                  slot has no history entry point of its own. */}
            </div>

            {/* Bottom row: service meta */}
            <div style={{ display: 'flex', gap: 12, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {[svc.service_code, svc.periodicity?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}
              </span>
            </div>

            {/* Task info section */}
            {task && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg-muted)', borderRadius: 6, border: '1px solid var(--border)' }}>
                {task.title && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word', whiteSpace: 'normal', marginBottom: 5 }}>
                    {task.title}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  {task.assigned_user_id && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                      <User size={11} />
                      {members.find(m => String(m.id) === String(task.assigned_user_id))?.name || `User #${task.assigned_user_id}`}
                    </span>
                  )}
                  {task.logged_hours != null && task.logged_hours > 0 && (() => {
                    const totalMins = Math.round(task.logged_hours * 60);
                    const h = Math.floor(totalMins / 60);
                    const m = totalMins % 60;
                    const label = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
                    return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>⏱ {label} logged</span>;
                  })()}
                  {task.completion_date && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Completion Date: {new Date(task.completion_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Tasks created via the Tasks module (New Task / Create Task with
                this service selected) — a service can have any number of
                these, unlike the single legacy slot above. This is now the
                real review mechanism: Approve/Reject act on the task itself
                via POST /tasks/:id/check. */}
            {(svc.linked_tasks || []).map(lt => (
              <LinkedTaskCard
                key={lt.id} lt={lt} svc={svc} appId={appId} agreementId={agreementId}
                finalApproved={finalApproved} isAdmin={isAdmin}
                navigate={navigate} api={api} onSaved={onSaved} onShowHistory={onShowHistory}
                taskModalMembers={taskModalMembers} taskModalRoles={taskModalRoles}
                taskModalAreasByRole={taskModalAreasByRole} myUserId={myUserId}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Create Task — the real Tasks module modal, pre-filled to this
          agreement + service. Reused as-is (not reimplemented) so task
          creation here always matches Tasks module behaviour, including its
          own checklist auto-population, no-op-edit rules, etc. */}
      <CreateTaskModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        members={taskModalMembers}
        roles={taskModalRoles}
        areasByRole={taskModalAreasByRole}
        defaultContractId={agreementId}
        defaultAgreementServiceId={svc.id}
        defaultAssigneeUserId={myUserId}
        defaultServiceMonth={month}
        defaultServiceYear={year}
        lockTaskType="contract"
        onCreated={async () => { onSaved(); }}
      />
    </div>
  );
}

/* ── Contract Task Create/Edit Form ──────────────────── */
function ContractTaskForm({ agreementId, members, myUserId, task, onSaved, onCancel }) {
  const { appId } = useParams();
  const api = useApi();
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [form, setForm]     = useState({
    title:            task?.title       || '',
    description:      task?.description || '',
    status:           task?.status      || 'pending',
    due_date:         task?.due_date    ? task.due_date.slice(0, 10) : '',
    assigned_user_id: task?.assigned_user_id || myUserId || '',
  });

  async function handleSubmit() {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError(null);
    try {
      const body = { ...form, agreement_id: agreementId };
      if (task?.id) {
        await api.updateContractTask(appId, task.id, body);
      } else {
        await api.createContractTask(appId, body);
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Title *</span>
        <input
          className="form-input"
          placeholder="Task title"
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Description</span>
        <textarea
          className="form-input"
          placeholder="Description (optional)"
          rows={2}
          style={{ resize: 'vertical' }}
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Status</span>
          <select className="form-input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Due Date</span>
          <input
            type="date"
            className="form-input"
            value={form.due_date}
            onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Assigned To</span>
          <SearchableSelect
            value={form.assigned_user_id}
            onChange={v => setForm(f => ({ ...f, assigned_user_id: v }))}
            options={[{ value: '', label: '— select —' }, ...members.map(m => ({ value: m.id, label: m.name }))]}
            showAll={false}
            btnClass="form-select-btn"
            matchTriggerWidth
          />
        </label>
      </div>
      {error && <div style={{ fontSize: 12, color: 'var(--neg)' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <button onClick={onCancel} style={{ padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, background: 'var(--bg-muted)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSubmit} disabled={saving} style={{ padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : task ? 'Update Task' : 'Create Task'}
        </button>
      </div>
    </div>
  );
}

/* ── Task side panel ──────────────────────────────────── */
function TaskPanel({ agreement, month, year, members, taskModalMembers, taskModalRoles, taskModalAreasByRole, isAdmin, isMember, myUserId, onClose, onSaved }) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [tab, setTab]             = useState('periodic');
  const [historySvc, setHistorySvc]   = useState(null);
  const [historyTask, setHistoryTask] = useState(null);
  const [showTaskForm, setShowTaskForm]        = useState(false);
  const [editingTask, setEditingTask]          = useState(null);
  const [finalApproving, setFinalApproving]    = useState(false);
  const [finalApproveErr, setFinalApproveErr]  = useState(null);
  const [reopenModalOpen, setReopenModalOpen]  = useState(false);
  const [reopenReason, setReopenReason]        = useState('');
  const [reopening, setReopening]              = useState(false);
  const [reopenErr, setReopenErr]              = useState(null);

  const periodicSvcs = agreement.periodic?.services || [];
  const oneTimeSvcs  = agreement.one_time?.services  || [];
  const allSvcs      = [...periodicSvcs, ...oneTimeSvcs];

  // Use the backend's own periodic/one_time done/total (based on each
  // service's linked_tasks), not the legacy single svc.task field — that
  // field goes stale once a service has multiple Tasks-module tasks, which
  // is what made this panel show "no task yet" / 0 completed even when the
  // service's real tasks (below) were Done and Approved.
  const totalServices = (agreement.periodic?.total || 0) + (agreement.one_time?.total || 0);
  const doneServices  = (agreement.periodic?.done  || 0) + (agreement.one_time?.done  || 0);
  const leftServices  = totalServices - doneServices;
  const score         = totalServices > 0 ? Math.round((doneServices / totalServices) * 100) : 0;

  function showServiceHistory(svc, lt) {
    setTab('history');
    setHistorySvc(svc);
    setHistoryTask(lt);
  }

  function backToPeriodic() {
    setTab('periodic');
    setHistorySvc(null);
    setHistoryTask(null);
  }

  async function handleFinalApprove() {
    setFinalApproving(true);
    setFinalApproveErr(null);
    try {
      await api.finalApproveAgreement(appId, agreement.id, { month, year });
      await onSaved?.();
    } catch (err) {
      setFinalApproveErr(err.response?.data?.error || err.message || 'Failed to confirm this month');
    } finally {
      setFinalApproving(false);
    }
  }

  async function handleReopen() {
    if (!reopenReason.trim()) { setReopenErr('A reason is required'); return; }
    setReopening(true);
    setReopenErr(null);
    try {
      await api.reopenAgreementMonth(appId, agreement.id, { month, year, reason: reopenReason.trim() });
      setReopenModalOpen(false);
      setReopenReason('');
      await onSaved?.();
    } catch (err) {
      setReopenErr(err.response?.data?.error || err.message || 'Failed to reopen this month');
    } finally {
      setReopening(false);
    }
  }

  const rm  = agreement.service_owner?.name || '—';
  const so  = agreement.service_owner?.name || '—';
  const relMgr = agreement.rm_user?.name || agreement.relationship_manager?.name || '—';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }} onClick={onClose} />

      <div style={{
        position: 'relative', zIndex: 1,
        width: 640,
        maxWidth: '100%',
        maxHeight: '100%',
        background: 'var(--bg-modal, var(--bg-card))',
        border: '1px solid var(--border)',
        borderRadius: 14,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg-modal, var(--bg-card))' }}>

          {/* Top row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: 1, marginRight: 12 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                boxShadow: '0 0 0 3px rgba(6,182,212,0.25), 0 0 20px rgba(6,182,212,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 18, color: '#fff', flexShrink: 0,
              }}>
                {(agreement.agreement_name || agreement.agreement_number || 'A').charAt(0)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--text-primary)', letterSpacing: '-0.3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {agreement.agreement_name || agreement.agreement_number || 'Agreement'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 700, letterSpacing: '1.2px', marginTop: 2 }}>
                  {MONTHS[month - 1].toUpperCase()} {year} · COMPLIANCE UPDATE
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--text-muted)', padding: '6px 7px', borderRadius: 8, display: 'flex', alignItems: 'center' }}>
              <X size={15} />
            </button>
          </div>

          {/* Meta row — 4 columns with dividers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
            {[
              { label: 'Client',       value: agreement.client?.client_name || '—', sub: agreement.client?.client_code },
              { label: 'Period',       value: agreement.start_date ? new Date(agreement.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—', sub: agreement.end_date ? `to ${new Date(agreement.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : '' },
              { label: 'Service Owner', value: so },
              { label: 'Rel. Manager',  value: relMgr },
            ].map((item, i) => (
              <div key={item.label} style={{ paddingRight: 12, paddingLeft: i > 0 ? 12 : 0, borderLeft: i > 0 ? '1px solid var(--border)' : 'none', minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.value}>{item.value}</div>
                {item.sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sub}</div>}
              </div>
            ))}
          </div>

          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 18 }}>
            {[
              { label: 'Total',     subLabel: 'Total Services',     value: totalServices, icon: '📋', accent: '#3b82f6', border: 'rgba(59,130,246,0.3)',  bg: 'rgba(59,130,246,0.08)' },
              { label: 'Done',      subLabel: 'Completed Services', value: doneServices,  icon: '✅', accent: '#22c55e', border: 'rgba(34,197,94,0.3)',   bg: 'rgba(34,197,94,0.08)' },
              { label: 'Left',      subLabel: 'Pending Services',   value: leftServices,  icon: '⏳', accent: '#f59e0b', border: 'rgba(245,158,11,0.3)',  bg: 'rgba(245,158,11,0.08)' },
            ].map(chip => (
              <div key={chip.label} style={{
                padding: '14px 16px', borderRadius: 12,
                background: chip.bg, border: `1px solid ${chip.border}`,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: chip.bg, border: `1px solid ${chip.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
                }}>{chip.icon}</div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: chip.accent, lineHeight: 1 }}>{chip.value}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.8px' }}>{chip.subLabel}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Month Completion</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: score === 100 ? '#22c55e' : 'var(--brand)' }}>{score}%</span>
            </div>
            <div style={{ height: 5, borderRadius: 3, background: 'var(--bg-muted)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 3, width: `${score}%`, background: score === 100 ? '#22c55e' : 'linear-gradient(90deg,#06b6d4,#3b82f6)', transition: 'width 0.4s' }} />
            </div>
          </div>

          {/* Final Approve — the one deliberate action that emails the
              client "agreement completed" for this month. Only shows once
              every service for this month is done+approved; nothing sends
              automatically just from approving individual tasks anymore. */}
          {agreement.final_approved && !agreement.can_final_approve && (
            <div style={{
              marginBottom: 16, padding: '9px 14px', borderRadius: 10,
              background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
              fontSize: 12.5, fontWeight: 600, color: '#16a34a',
            }}>
              ✓ Confirmed for {MONTHS[month - 1]} {year} — client has been emailed
            </div>
          )}

          {totalServices > 0 && doneServices === totalServices && agreement.can_final_approve && (
            <div style={{ marginBottom: 16 }}>
              {agreement.final_approved ? (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  padding: '9px 14px', borderRadius: 10,
                  background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                  fontSize: 12.5, fontWeight: 600, color: '#16a34a',
                }}>
                  <span>✓ Confirmed for {MONTHS[month - 1]} {year} — client has been emailed</span>
                  <button
                    onClick={() => { setReopenModalOpen(true); setReopenErr(null); }}
                    style={{
                      background: 'none', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 7,
                      padding: '4px 10px', fontSize: 11.5, fontWeight: 700, color: '#16a34a',
                      cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                    }}
                  >
                    Reopen
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleFinalApprove}
                    disabled={finalApproving}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10,
                      border: 'none', background: finalApproving ? '#16a34a99' : '#16a34a',
                      color: '#fff', fontSize: 13, fontWeight: 700,
                      cursor: finalApproving ? 'wait' : 'pointer',
                    }}
                  >
                    {finalApproving ? 'Confirming…' : `✓ Final Approve — Confirm ${MONTHS[month - 1]} ${year} & Email Client`}
                  </button>
                  {finalApproveErr && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444' }}>{finalApproveErr}</div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Only one section (Periodic) exists here, plus a per-service
              History drill-in — no tab bar needed. History shows a Back
              link instead. */}
          {tab === 'history' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 0 8px' }}>
              <button onClick={backToPeriodic} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: 'var(--brand)', padding: 0,
              }}>
                ← Back
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                · History
                {historySvc?.service_name ? ` — ${historySvc.service_name}` : ''}
                {historyTask ? ` → ${historyTask.title}${historyTask.task_number ? ` (${historyTask.task_number})` : ''}` : ''}
              </span>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: 'var(--bg-modal, var(--bg-card))' }}>
          {tab === 'periodic' && (() => {
            const displaySvcs = periodicSvcs;
            // A service has work tracked on it if it has any Tasks-module
            // task (linked_tasks) or, for older rows, the legacy single
            // task field — checking !s.task alone flagged services that
            // already have real linked_tasks as "no task yet".
            const noTaskCount = displaySvcs.filter(s => !s.task && (!s.linked_tasks || s.linked_tasks.length === 0)).length;
            return (
              <>
                {noTaskCount > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', marginBottom: 10,
                    background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8,
                    fontSize: 12, color: '#F57F17', fontWeight: 600,
                  }}>
                    <span style={{ fontSize: 14 }}>⚠</span>
                    {noTaskCount} service{noTaskCount !== 1 ? 's' : ''} have no task yet — click "+ Create Task" to add
                  </div>
                )}
                {displaySvcs.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>
                    No periodic services on this agreement.
                  </div>
                )}
                {displaySvcs.map(svc => (
                  <ServiceRow
                    key={svc.id}
                    svc={svc}
                    month={month}
                    year={year}
                    agreementId={agreement.id}
                    finalApproved={agreement.final_approved}
                    members={members}
                    taskModalMembers={taskModalMembers}
                    taskModalRoles={taskModalRoles}
                    taskModalAreasByRole={taskModalAreasByRole}
                    isAdmin={isAdmin}
                    isMember={isMember}
                    myUserId={myUserId}
                    onSaved={onSaved}
                    onShowHistory={showServiceHistory}
                  />
                ))}
                {displaySvcs.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', paddingTop: 6 }}>
                    Each service saves independently · <Clock size={10} style={{ display: 'inline' }} /> icon shows per-service history
                  </div>
                )}
              </>
            );
          })()}

          {/* History is per-task — same full activity log (created, status,
              assignee, deadline, checklist events) the Tasks module's own
              TimelinePanel shows, since a service can have multiple tasks
              now and each one needs its own timeline, not one shared log. */}
          {tab === 'history' && historyTask && (
            <TimelinePanel appId={appId} taskId={historyTask.id} members={taskModalMembers} />
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-modal, var(--bg-card))',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '11px 32px', borderRadius: 50, fontSize: 14, fontWeight: 700,
            background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
            color: '#fff', border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(6,182,212,0.4)',
          }}>
            <CheckCircle2 size={16} /> Close
          </button>
        </div>
      </div>

      {/* Reopen reason prompt — a mandatory reason is required and logged
          server-side, so there's a visible record of who reopened an
          already client-emailed month and why. */}
      {reopenModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => !reopening && setReopenModalOpen(false)} />
          <div style={{
            position: 'relative', zIndex: 1, width: 420, maxWidth: '100%',
            background: 'var(--bg-modal, var(--bg-card))', border: '1px solid var(--border)',
            borderRadius: 14, padding: 22, boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
          }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', marginBottom: 6 }}>
              Reopen {MONTHS[month - 1]} {year}?
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>
              The client has already been emailed that this agreement is complete for this month.
              Reopening lets you add or edit tasks again — you'll need to Final Approve a second time
              once everything's done, which sends a fresh confirmation email.
            </p>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Reason <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              value={reopenReason}
              onChange={e => { setReopenReason(e.target.value); setReopenErr(null); }}
              placeholder="e.g. Missed the ESI filing task before confirming"
              maxLength={500}
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'vertical',
                padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--border)',
                background: 'var(--bg-muted)', color: 'var(--text-primary)', fontSize: 13,
              }}
            />
            {reopenErr && <div style={{ marginTop: 8, fontSize: 12, color: '#ef4444' }}>{reopenErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => { setReopenModalOpen(false); setReopenReason(''); setReopenErr(null); }}
                disabled={reopening}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: 'var(--bg-muted)', color: 'var(--text-secondary)',
                  border: '1px solid var(--border)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReopen}
                disabled={reopening || !reopenReason.trim()}
                style={{
                  padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: reopening ? '#ef444499' : '#ef4444', color: '#fff',
                  border: 'none', cursor: reopening ? 'wait' : 'pointer',
                  opacity: !reopenReason.trim() ? 0.6 : 1,
                }}
              >
                {reopening ? 'Reopening…' : 'Reopen Month'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main page ────────────────────────────────────────── */
export default function ComplianceBoard() {
  const { appId } = useParams();
  const api = useApi();
  const { myRole, me } = useWorkspace();
  const isAdmin = myRole === 'super_admin' || myRole === 'admin';
  const isMember = myRole === 'member';
  const myUserId = me?.id;

  const now = new Date();
  const [month, setMonth]     = useState(now.getMonth() + 1);
  const [year,  setYear]      = useState(now.getFullYear());
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | approved
  const [data,  setData]      = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [panel,   setPanel]   = useState(null);
  const [members, setMembers] = useState([]);
  // Shaped for CreateTaskModal (user_id/first_name/last_name/email), unlike
  // `members` above which is the board's own simplified {id,name} shape.
  const [taskModalMembers, setTaskModalMembers] = useState([]);
  const [taskModalRoles, setTaskModalRoles]     = useState([]);
  const [taskModalAreasByRole, setTaskModalAreasByRole] = useState(new Map());

  // Build options from next month back through the last 12 months
  const monthOptions = [];
  for (let i = -1; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOptions.push({ value: `${d.getFullYear()}-${d.getMonth() + 1}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, m: d.getMonth() + 1, y: d.getFullYear() });
  }
  const selectedKey = `${year}-${month}`;

  useEffect(() => {
    api.listAppMembers(appId).then(d => {
      const arr = d?.items || d || [];
      setMembers(arr.map(m => ({
        id:   m.user_id || m.id,
        name: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || String(m.user_id || m.id),
      })));
      setTaskModalMembers(arr.map(m => ({
        user_id:    m.user_id || m.id,
        first_name: m.first_name,
        last_name:  m.last_name,
        email:      m.email,
      })));
    }).catch(() => {});
    api.roles.list(appId).then(r => {
      const roleRows = r.items || [];
      setTaskModalRoles(roleRows);
      Promise.all(
        roleRows.map(role =>
          api.listAreas(appId, role.id)
            .then(res => ({ role, items: res.items || [] }))
            .catch(() => ({ role, items: [] }))
        )
      ).then(perRole => {
        const map = new Map();
        perRole.forEach(({ role, items }) => map.set(role.id, items));
        setTaskModalAreasByRole(map);
      });
    }).catch(() => {});
  }, [appId, api]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await api.getComplianceBoard(appId, { month, year }));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [appId, month, year]);

  useEffect(() => { load(); }, [load]);

  // After save/review: reload board data. Panel syncs via the effect below.
  function onSaved() {
    load();
  }

  // Keep the open panel in sync with fresh board data after any reload
  useEffect(() => {
    if (panel && data.length > 0) {
      const updated = data.find(ag => ag.id === panel.id);
      if (updated) setPanel(updated);
    }
  }, [data]);

  // Whether an agreement still has required work outstanding, using the
  // backend's own periodic/one_time done/total counts (see the note by
  // hasPendingWork below in the row map for why not svc.task.status).
  // An agreement with zero services for the month is not "pending" — there
  // is nothing outstanding to do.
  function agreementHasPendingWork(ag) {
    const total = (ag.periodic?.total || 0) + (ag.one_time?.total || 0);
    const done  = (ag.periodic?.done  || 0) + (ag.one_time?.done  || 0);
    return done < total;
  }

  // "Approved" status filter: every required service for the month has been
  // reviewed and approved (not just marked done), using the same
  // periodic/one_time approved counts the Approved column shows.
  function agreementFullyApproved(ag) {
    const total    = (ag.periodic?.total    || 0) + (ag.one_time?.total    || 0);
    const approved = (ag.periodic?.approved || 0) + (ag.one_time?.approved || 0);
    return total > 0 && approved === total;
  }

  // "Awaiting Approval": every required service has at least one task
  // submitted (Done) but not every one of those has been reviewed/approved
  // yet — i.e. work is complete but sitting in the review queue. A service
  // only counts as backend-"done" once check_status is 'approved' (see
  // compliance_board.js isDone/isApproved), so "done but not approved"
  // isn't visible from the done/approved counts alone — check each
  // service's linked_tasks directly for a pending_review task.
  function agreementAwaitingApproval(ag) {
    const allSvcs = [...(ag.periodic?.services || []), ...(ag.one_time?.services || [])];
    if (allSvcs.length === 0) return false;
    return allSvcs.some(s => (s.linked_tasks || []).some(lt => lt.check_status === 'pending_review'));
  }

  const totalAgreements = data.length;
  const fullyDone       = data.filter(ag => !agreementHasPendingWork(ag) && ((ag.periodic?.total || 0) + (ag.one_time?.total || 0)) > 0).length;
  const allPeriodic  = data.reduce((s, ag) => s + (ag.periodic?.total || 0), 0);
  const donePeriodic = data.reduce((s, ag) => s + (ag.periodic?.done  || 0), 0);
  const allOneTime   = data.reduce((s, ag) => s + (ag.one_time?.total  || 0), 0);
  const doneOneTime  = data.reduce((s, ag) => s + (ag.one_time?.done   || 0), 0);
  const totalSvcs    = allPeriodic + allOneTime;
  const doneSvcs     = donePeriodic + doneOneTime;
  const overallPct   = totalSvcs > 0 ? Math.round((doneSvcs / totalSvcs) * 100) : 0;

  // "Pending" means work not yet done at all — excludes agreements that are
  // fully done but just sitting in the review queue (those are "Awaiting
  // Approval" instead), so the two options don't overlap.
  const visibleData = statusFilter === 'pending'  ? data.filter(ag => agreementHasPendingWork(ag) && !agreementAwaitingApproval(ag))
                     : statusFilter === 'awaiting_approval' ? data.filter(agreementAwaitingApproval)
                     : statusFilter === 'approved' ? data.filter(agreementFullyApproved)
                     : data;

  return (
    <div className="tl-page">
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 5 }}>Month</div>
          <select className="form-input" style={{ width: 180 }} value={selectedKey} onChange={e => {
            const opt = monthOptions.find(o => o.value === e.target.value);
            if (opt) { setMonth(opt.m); setYear(opt.y); }
          }}>
            {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 5 }}>Status</div>
          <select className="form-input" style={{ width: 220 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All agreements</option>
            <option value="pending">Pending</option>
            <option value="awaiting_approval">Awaiting Approval</option>
            <option value="approved">Approved</option>
          </select>
        </div>
        <button className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36 }} onClick={load}>
          <RefreshCw size={14} /> Refresh
        </button>
        <div style={{ marginLeft: 'auto', alignSelf: 'flex-end', fontSize: 12, color: 'var(--text-muted)' }}>
          {MONTHS[month - 1]} {year}
        </div>
      </div>

      {/* KPI Cards */}
      {!loading && data.length > 0 && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
          <KpiCard label="Overall Compliance" value={`${overallPct}%`} sub={`${doneSvcs} of ${totalSvcs} services done`} accent={overallPct >= 80 ? '#4caf50' : '#ef5350'} />
          <KpiCard label="Agreements"         value={`${fullyDone}/${totalAgreements}`} sub="fully completed" accent="#42A5F5" />
          <KpiCard label="Periodic"           value={`${donePeriodic}/${allPeriodic}`}  sub="services done"  accent="#AB47BC" />
          <KpiCard label="One-Time"           value={`${doneOneTime}/${allOneTime}`}    sub="services done"  accent="#26A69A" />
        </div>
      )}

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <div className="tl-loading"><span className="tl-spinner" /> Loading…</div>
      ) : (
        <div className="entity-table-wrap">
          <table className="entity-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Sl No</th>
                <th style={{ width: 200 }}>Agreement</th>
                <th style={{ width: 180 }}>Client</th>
                <th style={{ width: 160 }}>Service Owner</th>
                <th style={{ width: 100, textAlign: 'center' }}>Periodic</th>
                <th style={{ width: 90, textAlign: 'center' }}>Overall</th>
                <th style={{ width: 100, textAlign: 'center' }}>Approved</th>
                <th style={{ width: 80 }} className="row-actions"></th>
              </tr>
            </thead>
            <tbody>
              {visibleData.length === 0 ? (
                <tr><td colSpan={8}>
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>
                    {data.length === 0
                      ? `No active agreements for ${MONTHS[month - 1]} ${year}.`
                      : statusFilter === 'approved'
                        ? 'No fully approved agreements for this month.'
                        : statusFilter === 'awaiting_approval'
                          ? 'No agreements awaiting approval for this month.'
                          : 'No agreements with pending work for this month.'}
                  </div>
                </td></tr>
              ) : visibleData.map((ag, idx) => {
                // done/total come from the backend's own periodic/one_time
                // counts (based on each service's linked_tasks), not the
                // legacy single svc.task field — that field is stale once a
                // service has multiple Tasks-module tasks, which is why this
                // used to disagree with the Periodic/One-Time badges above it.
                const total     = (ag.periodic?.total || 0) + (ag.one_time?.total || 0);
                const done      = (ag.periodic?.done  || 0) + (ag.one_time?.done  || 0);
                // Same legacy-field issue as done/total above — approved
                // must come from the backend's own count, which checks
                // each service's linked_tasks for check_status='approved'.
                const approved  = (ag.periodic?.approved || 0) + (ag.one_time?.approved || 0);
                const overallAg = total > 0 ? Math.round((done / total) * 100) : 0;
                const pColor    = overallAg === 100 ? '#388E3C' : 'var(--text-primary)';
                const startStr  = ag.start_date ? new Date(ag.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
                const endStr    = ag.end_date   ? new Date(ag.end_date).toLocaleDateString('en-GB',   { day: '2-digit', month: 'short', year: '2-digit' }) : '';

                return (
                  <tr
                    key={ag.id}
                    onClick={() => setPanel(ag)}
                    style={{ cursor: 'pointer' }}
                    className="clickable-row"
                  >
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                    <td title={ag.agreement_name || ag.agreement_number}>
                      <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'normal', wordBreak: 'break-word' }}>{ag.agreement_name || ag.agreement_number}</div>
                      {ag.agreement_name && <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--brand)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ag.agreement_number}</div>}
                      {startStr && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{startStr}{endStr ? ` – ${endStr}` : ''}</div>}
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>{ag.client?.client_name || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13, whiteSpace: 'normal', wordBreak: 'break-word' }}>{ag.service_owner?.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ textAlign: 'center' }}><Badge done={ag.periodic?.done || 0} total={ag.periodic?.total || 0} color="#F57C00" /></td>
                    <td style={{ textAlign: 'center', fontWeight: 700, fontSize: 13, color: pColor }}>{total === 0 ? '—' : `${overallAg}%`}</td>
                    <td style={{ textAlign: 'center' }}><Badge done={approved} total={total} color="#1565C0" /></td>
                    <td onClick={e => e.stopPropagation()}>
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: 12, padding: '5px 12px', whiteSpace: 'nowrap' }}
                        onClick={() => setPanel(ag)}>
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {panel && (
        <TaskPanel
          agreement={panel}
          month={month}
          year={year}
          members={members}
          taskModalMembers={taskModalMembers}
          taskModalRoles={taskModalRoles}
          taskModalAreasByRole={taskModalAreasByRole}
          isAdmin={isAdmin}
          isMember={isMember}
          myUserId={myUserId}
          onClose={() => setPanel(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
